import React, { useState } from 'react';
import styled from 'styles/styled';

interface TimerDisplayProps {
  timeLeft: string;
  isWarning?: boolean;
}

const TimerDisplay: React.FC<TimerDisplayProps> = ({ timeLeft, isWarning = false }) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const toggleTooltip = () => setIsTooltipVisible(prev => !prev);
  const hideTooltip = () => setIsTooltipVisible(false);

  return (
    <Wrapper>
      <TimerBar $isWarning={isWarning}>
        <TimerText>Time left to make the payment</TimerText>
        <TimeValue $isWarning={isWarning}>{timeLeft}</TimeValue>
      </TimerBar>
      <InfoWrapper>
        <InfoIcon
          type="button"
          onClick={toggleTooltip}
          onBlur={hideTooltip}
          aria-label="Payment time information"
          aria-pressed={isTooltipVisible}
        >
          ⓘ
        </InfoIcon>
        {isTooltipVisible && (
          <Tooltip>
            You must pay within this time, or your tournament will not be created (timeout).
          </Tooltip>
        )}
      </InfoWrapper>
    </Wrapper>
  );
};

// Centering is delegated to the parent (HeaderStack on PaymentQR uses
// align-items: center; Belt on WalletAddressEntry uses justify-content: center).
// We don't set margin: auto here because in flex contexts auto margins steal
// available space and push siblings to the edges.
const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 720px) {
    gap: 6px;
  }
`;

// DS countdown spec (preview/components-countdown.html):
// bg #292B2E, border #52D381, glow shadow, 22px / 700, tabular-nums.
const TimerBar = styled.div<{ $isWarning?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #292B2E;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid ${({ $isWarning }) => ($isWarning ? '#FF6782' : '#52D381')};
  box-shadow: ${({ $isWarning }) =>
    $isWarning
      ? '0 0 0 1px rgba(255, 103, 130, 0.25), 0 8px 24px rgba(255, 103, 130, 0.18)'
      : '0 0 0 1px rgba(82, 211, 129, 0.25), 0 8px 24px rgba(82, 211, 129, 0.18)'};
  transition: border-color 200ms ease-in, box-shadow 200ms ease-in;

  @media (max-width: 720px) {
    padding: 6px 10px;
    gap: 8px;
  }
`;

const TimerText = styled.span`
  font-size: 14px;
  color: #EFEFEF;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;

  @media (max-width: 720px) {
    font-size: 12px;
  }
`;

const TimeValue = styled.span<{ $isWarning: boolean }>`
  font-size: 22px;
  color: ${props => (props.$isWarning ? '#FF6782' : '#EFEFEF')};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  line-height: 1;

  @media (max-width: 720px) {
    font-size: 18px;
  }
`;

const InfoWrapper = styled.div`
  position: relative;
`;

const InfoIcon = styled.button`
  font-size: 10px;
  color: #EFEFEF;
  background: transparent;
  border: 1.5px solid #EFEFEF;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;

  &:hover,
  &:focus-visible {
    opacity: 0.85;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 240px;
  padding: 10px 12px;
  background: #292B2E;
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  color: #EFEFEF;
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  z-index: 2;
`;

export default TimerDisplay;
